// ===========================================================================
// ReHome — Kliensoldali képátméretezés feltöltés előtt
//
// MIÉRT A KLIENSEN: egy telefonos fotó 3-8 MB. Ha nyersen töltjük fel, azt
// megfizetjük kétszer — egyszer a menhelyi dolgozó mobilnetjén (a feltöltés
// az, ami lassú), egyszer pedig minden egyes megjelenítéskor a látogatók
// sávszélességén. A szerveroldali átméretezés a feltöltés költségét nem
// spórolja meg, csak a tárolásét. Itt vágva mindkettő megspórolódik.
//
// A hosszabbik él 720 pixelre korlátozódik, aránytartóan. Ez a lista- és
// profilnézetekhez bőven elég (a legnagyobb megjelenítés is jóval kisebb),
// nagyítást pedig sosem végzünk: a kisebb kép változatlanul megy tovább.
// ===========================================================================

/** A hosszabbik él maximuma pixelben. */
export const MAX_IMAGE_EDGE = 720;

/** JPEG minőség. 0.82 a vizuálisan veszteségmentes és a fájlméret határa. */
export const IMAGE_QUALITY = 0.82;

/**
 * Az átméretezett dimenziók — aránytartóan, felfelé SOHA nem skálázva.
 * Külön kiemelve, hogy tesztelhető legyen böngésző (canvas) nélkül is.
 */
export const computeResizeTarget = (
  width: number,
  height: number,
  maxEdge: number = MAX_IMAGE_EDGE
): { width: number; height: number; changed: boolean } => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width, height, changed: false };
  }
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) return { width, height, changed: false };

  const scale = maxEdge / longEdge;
  // Kerekítés után is legalább 1 px — egy extrém panoráma rövidebb éle
  // különben 0-ra kerekedne, és a canvas hibát dobna.
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    changed: true,
  };
};

/** Egy dekódolás sem tarthat ennél tovább — l. `loadImage`. */
const DECODE_TIMEOUT_MS = 10_000;

/**
 * A dekódolás IDŐKORLÁTOS. Egy sérült vagy a futtatókörnyezet által
 * egyszerűen figyelmen kívül hagyott kép esetén (pl. jsdom, ahol sem az
 * `onload`, sem az `onerror` nem sül el) a nyitva maradt Promise az egész
 * feltöltést megakasztaná — az pedig sokkal rosszabb, mint egy kihagyott
 * átméretezés.
 */
const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error('A kép dekódolása túllépte az időkorlátot.')), DECODE_TIMEOUT_MS);
    const done = (fn: () => void) => { clearTimeout(timer); fn(); };
    img.onload = () => done(() => resolve(img));
    img.onerror = () => done(() => reject(new Error('A kép nem dekódolható.')));
    img.src = dataUrl;
  });

/**
 * Átméretezi a data URL-t úgy, hogy a hosszabbik éle legfeljebb `maxEdge`
 * legyen. Az eredmény mindig JPEG — a feltöltési útvonal amúgy is `.jpg`
 * néven tárol, így a kiterjesztés és a tényleges tartalom itt kerül végre
 * szinkronba.
 *
 * HIBATŰRŐ: ha nincs canvas (SSR, teszt), vagy a dekódolás nem sikerül, az
 * EREDETI data URL-t adja vissza. Egy átméretezési hiba miatt nem veszhet el
 * egy bekerülési fotó — a nagy kép rosszabb, de nem hiba.
 */
export const resizeDataUrl = async (
  dataUrl: string,
  maxEdge: number = MAX_IMAGE_EDGE,
  quality: number = IMAGE_QUALITY
): Promise<string> => {
  if (!dataUrl || !dataUrl.startsWith('data:image/')) return dataUrl;
  // Az SVG vektoros — a raszterizálás rontana rajta, nem javítana.
  if (dataUrl.startsWith('data:image/svg')) return dataUrl;
  if (typeof document === 'undefined' || typeof Image === 'undefined') return dataUrl;

  // A canvas ELŐBB készül el, mint ahogy a képet betöltenénk: ha nincs 2D
  // kontextus (SSR-szerű környezet, jsdom canvas nélkül), akkor a dekódolásra
  // sem érdemes várni.
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext ? canvas.getContext('2d') : null;
  if (!ctx) return dataUrl;

  try {
    const img = await loadImage(dataUrl);
    const target = computeResizeTarget(img.naturalWidth, img.naturalHeight, maxEdge);
    if (!target.changed) return dataUrl;

    canvas.width = target.width;
    canvas.height = target.height;

    // Áttetsző PNG-ből JPEG-be a fedetlen rész feketévé válna — fehér alap.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, target.width, target.height);
    ctx.drawImage(img, 0, 0, target.width, target.height);

    const out = canvas.toDataURL ? canvas.toDataURL('image/jpeg', quality) : '';
    // Egy elrontott canvas üres data URL-t is adhat — akkor inkább az eredetit.
    return out && out.length > 'data:image/jpeg;base64,'.length ? out : dataUrl;
  } catch {
    return dataUrl;
  }
};
